import { DefaultTheme } from 'styled-components';
import { ColorEnum, ThemeEnum } from '@app/common';
import { darken, getLuminance, lighten } from 'polished';
import { getOpacity } from '../utils/get-rgb';
import { assetColors } from '../asset-colors';

const ASSET_TEXT_LUMINANCE_THRESHOLD = 0.44;

const createTone = (baseColor: string): DefaultTheme['assetColor'][ColorEnum.Blue] => {
    const isVeryLight = getLuminance(baseColor) > ASSET_TEXT_LUMINANCE_THRESHOLD;

    return {
        main: baseColor,
        activeBackground: isVeryLight ? darken(0.1, baseColor) : getOpacity(baseColor, 0.8),
        dark: darken(0.1, baseColor),
        darken: darken(0.05, baseColor),
        light: lighten(0.1, baseColor),
        lighten: lighten(0.2, baseColor),
        opacity: {
            low: getOpacity(baseColor, 0.5),
            medium: getOpacity(baseColor, 0.24),
            high: getOpacity(baseColor, 0.16),
        },
        text: isVeryLight ? getOpacity('#000000', 0.6) : getOpacity('#ffffff', 0.8),
    };
};

export const createAssetColor = (t: ThemeEnum) => {
    const paletteByTheme = assetColors[t];

    const palette: DefaultTheme['assetColor'] = {
        [ColorEnum.Blue]: createTone(paletteByTheme.blue.main),
        [ColorEnum.BlueLight]: createTone(paletteByTheme.blue.light),
        [ColorEnum.BlueDark]: createTone(paletteByTheme.blue.dark),
        [ColorEnum.Purple]: createTone(paletteByTheme.purple.main),
        [ColorEnum.PurpleLight]: createTone(paletteByTheme.purple.light),
        [ColorEnum.PurpleDark]: createTone(paletteByTheme.purple.dark),
        [ColorEnum.Green]: createTone(paletteByTheme.green.main),
        [ColorEnum.GreenLight]: createTone(paletteByTheme.green.light),
        [ColorEnum.GreenDark]: createTone(paletteByTheme.green.dark),
        [ColorEnum.Primary]: createTone(paletteByTheme.primary.main),
        [ColorEnum.PrimaryLight]: createTone(paletteByTheme.primary.light),
        [ColorEnum.PrimaryDark]: createTone(paletteByTheme.primary.dark),
        [ColorEnum.Secondary]: createTone(paletteByTheme.secondary.main),
        [ColorEnum.SecondaryLight]: createTone(paletteByTheme.secondary.light),
        [ColorEnum.SecondaryDark]: createTone(paletteByTheme.secondary.dark),
        [ColorEnum.Gray]: createTone(paletteByTheme.gray.main),
        [ColorEnum.GrayLight]: createTone(paletteByTheme.gray.light),
        [ColorEnum.GrayDark]: createTone(paletteByTheme.gray.dark),
        [ColorEnum.Orange]: createTone(paletteByTheme.orange.main),
        [ColorEnum.OrangeLight]: createTone(paletteByTheme.orange.light),
        [ColorEnum.OrangeDark]: createTone(paletteByTheme.orange.dark),
        [ColorEnum.Red]: createTone(paletteByTheme.red.main),
        [ColorEnum.RedLight]: createTone(paletteByTheme.red.light),
        [ColorEnum.RedDark]: createTone(paletteByTheme.red.dark),
        [ColorEnum.Pink]: createTone(paletteByTheme.pink.main),
        [ColorEnum.PinkLight]: createTone(paletteByTheme.pink.light),
        [ColorEnum.PinkDark]: createTone(paletteByTheme.pink.dark),
        [ColorEnum.Brown]: createTone(paletteByTheme.brown.main),
        [ColorEnum.BrownLight]: createTone(paletteByTheme.brown.light),
        [ColorEnum.BrownDark]: createTone(paletteByTheme.brown.dark),
        [ColorEnum.Yellow]: createTone(paletteByTheme.yellow.main),
        [ColorEnum.YellowLight]: createTone(paletteByTheme.yellow.light),
        [ColorEnum.YellowDark]: createTone(paletteByTheme.yellow.dark),
    };

    return palette;
};
